import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { type VerifySessionResponse } from '@/types/Auth';
import { crmOriginOf, isEmbedPath, isEmbedSession } from '@/utils/b2coreEmbed';
import useAuth from './useAuth';
import useAxiosInterceptors from './useAxiosInterceptors';
import { useEntityConfig } from './useEntityConfig';

const useValidate = (setIsLoading: (isLoading: boolean) => void) => {
  const { setToken } = useAuth();
  const axiosPrivate = useAxiosInterceptors();
  const config = useEntityConfig();
  const { pathname } = useLocation();

  useEffect(() => {
    const isEmbed = isEmbedPath(pathname) || isEmbedSession(crmOriginOf(config.VITE_CLIENT_PORTAL_URL));

    if (pathname === '/oauth/callback' || isEmbed) {
      setIsLoading(false);

      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const validate = async () => {
      try {
        const response = await axiosPrivate.get<VerifySessionResponse>('/auth/validate', {
          signal: controller.signal,
        });

        if (isMounted && !response?.data?.valid) {
          setToken(undefined);
        }
      } catch {
        setToken(undefined);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    validate();

    return () => {
      isMounted = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useValidate;
